/* eslint-disable react/forbid-prop-types */
import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Expander from "../../../../../components/Expander";
import helpers from "../../../../../common/utils/utils";
import {
  displayVarsomePremiumLogo,
  getVarsoUrl,
} from "../../../../../common/helpers/helpers";
import withErrorGuard from "../../../../../HOC/withErrorGuard";
import ComponentPanel from "../../../../../components/atoms/ComponentPanel/ComponentPanel";
import { displayYesNo, getOtherInformationRow } from "./helpers";
import { getIsUserPremium } from "../../../../../state/selectors/general.selectors";
import HelperText from "../../../../../components/atoms/HelperText";

const { bool, string, shape, any, number, arrayOf } = PropTypes;

const DGI = ({ data, isUserPremium }) => {
  const [, updateState] = React.useState();
  const forceUpdate = useCallback(() => updateState({}), []);

  const expandRow = (row) => {
    row.expanded = !row.expanded;
    forceUpdate();
  };

  const getRow = (item, key) => {
    let row = null;
    const rows = [];
    if (item) {
      row = (
        <tr key={key}>
          <td>
            <a
              target="_blank"
              href={getVarsoUrl(
                `https://dgidb.org/results?searchType=drug&searchTerms=${item.drug_name_primary}`
              )}
            >
              {item.drug_name_primary}
            </a>
            {item.drug_chembl_id && (
              <a
                target="_blank"
                href={getVarsoUrl(
                  `https://www.ebi.ac.uk/chembl/compound_report_card/${item.drug_chembl_id}`
                )}
              >
                {` (${item.drug_chembl_id})`}
              </a>
            )}
          </td>
          <td>
            {item.disease && item.disease.length !== 0
              ? item.disease.map((obj, i, arr) => {
                  const m = `${obj.name}-${i}`;
                  return (
                    <span key={m}>
                      {helpers.capitalizeFirstLetter(obj.name)}
                      {i < arr.length - 1 ? ", " : ""}
                    </span>
                  );
                })
              : "-"}
          </td>
          <td>{displayYesNo(item, "drug_fda_approved")}</td>
          <td>{displayYesNo(item, "drug_immunotherapy")}</td>
          <td>{displayYesNo(item, "drug_anti_neoplastic")}</td>
          <td style={{ textAlign: "center" }}>
            <a onClick={() => expandRow(item)}>
              {item.expanded ? "Hide details" : "More details..."}
            </a>
          </td>
        </tr>
      );
      rows.push(row);
    }

    const interactionClaims = item.interaction_claims?.[0];

    if (item.expanded) {
      const expandedRow = (
        <tr key={`row-expanded-${key}`}>
          <td colSpan="6">
            <div>
              <p style={{ marginBottom: "0.3rem" }}>
                <strong>Other Information:</strong>
              </p>
              <table
                className="varsome-table"
                style={{ wordWrap: "break-word", overflowWrap: "break-word" }}
              >
                <tbody>
                  <tr key={`interaction-row-${key}`}>
                    <td width="20%">Interaction Type</td>
                    <td>
                      {item.interaction_type &&
                      item.interaction_type.length !== 0
                        ? item.interaction_type.map((obj, i, arr) => {
                            const tdKey = `${i}-td`;
                            return (
                              <span key={tdKey}>
                                {" "}
                                {helpers.capitalizeFirstLetter(obj)}
                                {i < arr.length - 1 ? ", " : ""}
                              </span>
                            );
                          })
                        : " N/A"}
                    </td>
                  </tr>
                  {item.attributes &&
                    item.attributes.length !== 0 &&
                    item.attributes.forEach((obj, i) => {
                      Object.keys(obj).forEach((prop) => {
                        return getOtherInformationRow(i, obj, prop);
                      });
                    })}
                  {interactionClaims &&
                    Object.keys(interactionClaims).map((prop) => {
                      return getOtherInformationRow(0, interactionClaims, prop);
                    })}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      );
      rows.push(expandedRow);
    }
    return rows;
  };

  const processItems = (items) => {
    const sortedItems = items?.filter((item) => {
      return item.drug_name_primary !== null;
    });
    sortedItems?.sort((a, b) => {
      const nameA = a.drug_name_primary.toUpperCase();
      const nameB = b.drug_name_primary.toUpperCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
    return sortedItems;
  };

  let content = null;
  if (isUserPremium || __IS_COMPONENTS_PORTAL__) {
    if (data && Object.keys(data || {}).length > 0) {
      const items = processItems(data?.items?.slice());
      const rows = items?.map((item, i) => {
        return getRow(item, i);
      });
      if (rows && rows.length > 0) {
        content = (
          <div className="varsome-inner-container" data-testid="dgi-component">
            <table className="varsome-table tw-break-word">
              <thead>
                <tr>
                  <th width="20%">
                    Drug name primary
                    <HelperText fieldDescriptionKey="Drug name primary" />
                  </th>
                  <th width="25%">Disease</th>
                  <th width="15%">FDA Approved</th>
                  <th width="15%">Immunotherapy</th>
                  <th width="15%">Antineoplastic</th>
                  <th width="10%">Details</th>
                </tr>
              </thead>
              <Expander showMore="10" columns="6" component="tbody">
                {rows}
              </Expander>
            </table>
          </div>
        );
      }
      return (
        <div className="varsome-result-fixed" id="dgi_component_varsome">
          <div className="varsome-container varsome-container__basic">
            <ComponentPanel
              title="DGI"
              version={data?.version}
              url="https://www.dgidb.org/"
              premiumLogo={displayVarsomePremiumLogo(false)}
              content={content}
            />
          </div>
        </div>
      );
    }
    return null;
  }
  return null;
};

const mapStateToProps = (state) => ({
  isUserPremium: getIsUserPremium(state),
});

DGI.propTypes = {
  data: shape({
    items: arrayOf(
      shape({
        attributes: any,
        disease: arrayOf(
          shape({
            id: number,
            name: string,
            normalized_cancer: string,
          })
        ),
        drug_anti_neoplastic: bool,
        drug_chembl_id: string,
        drug_fda_approved: bool,
        drug_immunotherapy: bool,
        drug_name_primary: string,
        gene_entrez: number,
        gene_symbol: string,
        id: string,
        interaction_type: any,
      })
    ),
    version: string,
  }),
  isUserPremium: PropTypes.bool,
};

DGI.defaultProps = {
  data: null,
  isUserPremium: false,
};

export default connect(mapStateToProps)(withErrorGuard(DGI, "DGI", "DGI"));
